import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';

/**
 * Context para manejar la invalidación de caché y actualización automática de datos.
 * Cuando se elimina o restaura un registro, se notifica a todos los componentes
 * que están escuchando para que refresquen sus datos.
 */
const DataRefreshContext = createContext();

// Tipos de entidades que pueden ser invalidadas
export const ENTITY_TYPES = {
  CLIENTES: 'clientes',
  COTIZACIONES: 'cotizaciones',
  VENTAS: 'ventas',
  PUNTOS: 'puntos',
  ALL: 'all'
};

export const DataRefreshProvider = ({ children }) => {
  // Timestamps de última modificación por entidad
  const [refreshTimestamps, setRefreshTimestamps] = useState({
    clientes: Date.now(),
    cotizaciones: Date.now(),
    ventas: Date.now(),
    puntos: Date.now()
  });

  // Contador global para forzar re-render cuando es necesario
  const [globalRefreshCounter, setGlobalRefreshCounter] = useState(0);
  const canalRef = useRef(null);

  /**
   * Invalida el caché de una o más entidades.
   * Los componentes que escuchan estos tipos se actualizarán automáticamente.
   * 
   * @param {string|string[]} entityTypes - Tipo(s) de entidad a invalidar
   */
  const invalidateCache = useCallback((entityTypes) => {
    const types = Array.isArray(entityTypes) ? entityTypes : [entityTypes];
    const now = Date.now();

    setRefreshTimestamps(prev => {
      const updated = { ...prev };
      
      types.forEach(type => {
        if (type === ENTITY_TYPES.ALL) {
          // Invalidar todas las entidades
          Object.keys(updated).forEach(key => {
            updated[key] = now;
          });
        } else if (updated[type] !== undefined) {
          updated[type] = now;
        }
      });
      
      return updated;
    });

    // Incrementar contador global para casos donde se necesita forzar re-render
    setGlobalRefreshCounter(prev => prev + 1);
  }, []);

  /**
   * Notifica que se eliminó un registro.
   * Invalida las entidades relacionadas.
   * 
   * @param {string} entityType - Tipo de entidad eliminada
   * @param {object} metadata - Información adicional (ej: cliente_id para actualizar ficha)
   */
  const notifyDeletion = useCallback((entityType, metadata = {}) => {
    const relatedEntities = [entityType];
    
    // Determinar entidades relacionadas que también deben actualizarse
    switch (entityType) {
      case ENTITY_TYPES.VENTAS:
        // Al eliminar venta, actualizar también clientes (ficha) y puntos
        relatedEntities.push(ENTITY_TYPES.CLIENTES, ENTITY_TYPES.PUNTOS);
        break;
      case ENTITY_TYPES.COTIZACIONES:
        // Al eliminar cotización, actualizar clientes (ficha)
        relatedEntities.push(ENTITY_TYPES.CLIENTES);
        break;
      case ENTITY_TYPES.CLIENTES:
        // Al eliminar cliente, actualizar cotizaciones y ventas
        relatedEntities.push(ENTITY_TYPES.COTIZACIONES, ENTITY_TYPES.VENTAS);
        break;
      default:
        break;
    }
    
    invalidateCache(relatedEntities);
    canalRef.current?.postMessage(relatedEntities);
    
    // Log para debugging
    console.log(`[DataRefresh] Entidades invalidadas: ${relatedEntities.join(', ')}`);
  }, [invalidateCache]);

  // Sincroniza las demás pestañas abiertas del panel
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const canal = new BroadcastChannel('jpm-data-refresh');
    canal.onmessage = (e) => invalidateCache(e.data);
    canalRef.current = canal;
    return () => { canal.close(); canalRef.current = null; };
  }, [invalidateCache]);

  /**
   * Notifica que se restauró un registro.
   * Tiene el mismo efecto que notifyDeletion.
   */
  const notifyRestoration = useCallback((entityType, metadata = {}) => {
    notifyDeletion(entityType, metadata);
  }, [notifyDeletion]);

  /**
   * Obtiene el timestamp de última modificación de una entidad.
   * Útil para usar como dependencia en useEffect.
   */
  const getRefreshTimestamp = useCallback((entityType) => {
    return refreshTimestamps[entityType] || 0;
  }, [refreshTimestamps]);

  /**
   * Hook helper para suscribirse a cambios de una entidad.
   * Retorna un valor que cambia cuando la entidad es invalidada.
   */
  const useRefreshKey = (entityType) => {
    return refreshTimestamps[entityType] || globalRefreshCounter;
  };

  const value = useMemo(() => ({
    refreshTimestamps,
    globalRefreshCounter,
    invalidateCache,
    notifyDeletion,
    notifyRestoration,
    getRefreshTimestamp,
    useRefreshKey,
    ENTITY_TYPES
  }), [
    refreshTimestamps,
    globalRefreshCounter,
    invalidateCache,
    notifyDeletion,
    notifyRestoration,
    getRefreshTimestamp
  ]);

  return (
    <DataRefreshContext.Provider value={value}>
      {children}
    </DataRefreshContext.Provider>
  );
};

/**
 * Hook para usar el contexto de DataRefresh.
 */
export const useDataRefresh = () => {
  const context = useContext(DataRefreshContext);
  if (!context) {
    throw new Error('useDataRefresh must be used within a DataRefreshProvider');
  }
  return context;
};

/**
 * Hook helper que retorna una key de refresh para usar en useEffect.
 * Cuando la entidad es invalidada, la key cambia y el efecto se re-ejecuta.
 * 
 * @param {string} entityType - Tipo de entidad a observar
 * @returns {number} - Key que cambia cuando la entidad es invalidada
 */
export const useRefreshTrigger = (entityType) => {
  const { refreshTimestamps, globalRefreshCounter } = useDataRefresh();
  return refreshTimestamps[entityType] || globalRefreshCounter;
};

export default DataRefreshContext;
